/**
 * Display jobs for a given project created by the user.
 */

import React, { useEffect, useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { push, replace } from "connected-react-router"
import { Button } from "dash-styled"
import { TableColumn } from "resha"
import { metaAI } from "neql"

import {
    loadMoreJobList,
    loadMoreJobFeedbackMessageList
} from "../../../../../../Actions/API/Job/JobActions"
import {
    getJobWithFeedbackStats,
    stopMonitorProcessingBatchList,
    startMonitorProcessingBatchList,
    retryAsyncOperation,
    archiveAsyncOperation
} from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { clearDownloadAllLink } from "../../../../../../Actions/API/Job/JobDownloadActions"

import { MainThunkDispatch } from "../../../../../../Reducers"
import { routePaths } from "../../../../../../Routes"
import { selectHasFreemiumExpired } from "../../../../../../Helpers/Functions/Selectors/auth"

import {
    APIJobModel,
    APIJobListParams,
    isCancelableJob,
    isRelabelableJob,
    getJobID,
    isEnqueueableJob,
    APIJobQueryModel,
    isDeletableJob
} from "../../../../../../Models/API/JobModel"
import {
    FetchState,
    hasValue,
    getValue,
    hasResult
} from "../../../../../../Models/Client/FetchState"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { FilterExpression } from "../../../../../../Models/Client/FilterExpression"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { Result } from "../../../../../../Models/Utility/Result"
import { getValueById } from "../../../../../../Models/Client/RemoteData"
import { isAppInstanceEnabled } from "../../../../../../Models/API/AppInstanceModel"

import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useSelection } from "../../../../../../Hooks/useSelection"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { useCanLabelWebSocket } from "../../../../../../Hooks/useCanLabelWebSocket"
import { useAppDetailsPaths } from "../../../../../../Hooks/useAppDetailsPaths"
import { useAIModelAssignmentStatus } from "../../../../../../Hooks/useAIModelAssignmentStatus"
import { useJobTotalCount } from "../../../../../../Hooks/useJobTotalCount"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { useAppAsyncOperations } from "../../../../../../Hooks/useAppAsyncOperations"
import { useCanUploadJobs } from "../../../../../../Hooks/useCanUploadJobs"
import { useJobFilterParams } from "../../../../../../Hooks/useJobFilterParams"
import { useJobDownloadOperation } from "../../../../../../Hooks/useJobDownloadOperation"
import { useJobCountWithFilters } from "../../../../../../Hooks/useJobCountWithFilters"

import { BatchStatusBanner } from "../../../Common/BatchStatusBanner/BatchStatusBanner"
import { AsyncOperationStatusBanner } from "../../../Common/AsyncOperationStatusBanner"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import {
    jobSortOptions,
    jobWithMessagesSortOptions,
    modelScoreSortOptions
} from "../Common/constants"
import { DetailView } from "../DetailView/DetailView"
import {
    AppDetailBase,
    AppDetailBaseProps
} from "../AppDetailBase/AppDetailBase"
import {
    JobsBatchActionType,
    JobsBatchActionModal
} from "./JobsBatchActionModal"
import { JobsListReviewButton } from "./JobsListReviewButton"
import { ColumnsProps, JobsListTable } from "./JobsListTable/JobsListTable"
import { FeedbackTagsHeader } from "./FeedbackTagsHeader"

export type JobsListType<M extends APIJobModel> = FetchState<{
    items: M[]
    total: number
}>

export interface JobsListRenderingProps<M extends APIJobModel> {
    jobListFetch?: JobsListType<M>
    columns: (values: ColumnsProps<M>) => TableColumn<M>[]
}

export interface JobsListProps {
    orgUsername: string
    appID: string
    params: APIJobListParams
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    selected?: string[]
    /**
     * ID of the job to show detail modal.
     * `undefined` means no need to show modal.
     * `null` indicates that the requester wants to show the first item of the list once the list is fetched.
     */
    jobID?: string | null
    version?: string | null
    taskID?: string | null
    clearFilterOnCloseModal: boolean
    onVisibleItemsChanged: (ids: string[]) => void
    isReviewView: boolean
    selectedJob?: string | null
}

export type Props<M extends APIJobModel> = JobsListProps &
    JobsListRenderingProps<M>

export const JobsList = <M extends APIJobModel>({
    orgUsername,
    appID,
    filterAndSort,
    params,
    jobID,
    version,
    taskID,
    clearFilterOnCloseModal,
    selected,
    jobListFetch,
    isReviewView,
    selectedJob,
    columns,
    onVisibleItemsChanged
}: Props<M>) => {
    const [dialog, setDialog] =
        useState<JobsBatchActionType | "download" | undefined>(undefined)
    const hideDialog = useCallback(() => setDialog(undefined), [])
    const confirmDownload = useCallback(() => setDialog("download"), [])
    const [navigationMode, setNavigationMode] =
        useState<"all" | "selected">("all")
    const dispatch = useDispatch<MainThunkDispatch>()
    const instanceFetch = useAppInstance(appID)
    const [useSelectedParam, , noSelected] = useBoolean(true)
    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const [
        batchInProgressDialog,
        showBatchInProgressDialog,
        hideBatchInProgressDialog
    ] = useBoolean(false)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

    const uploadPath = routePaths.appAddData(orgUsername, appID, "input")
    const { upload_allowed, remaining_count } = useCanUploadJobs(appID)

    const handleUploadData = useCallback(() => {
        !upload_allowed && remaining_count !== undefined && remaining_count > 0
            ? showBatchInProgressDialog()
            : hasFreemiumPlanExpired || !upload_allowed
            ? showUserExpiryContact()
            : dispatch(push(uploadPath))
    }, [
        dispatch,
        hasFreemiumPlanExpired,
        remaining_count,
        showBatchInProgressDialog,
        showUserExpiryContact,
        uploadPath,
        upload_allowed
    ])

    const activeLearningStatusFetch = useAIModelAssignmentStatus(
        appID,
        metaAI.Meta_Ai_Assignment_Enum.ActiveLearning
    )

    const [
        isMessagesPath,
        appDetailPath,
        jobsDetailPath,
        jobsDetailReviewPath
    ] = useAppDetailsPaths()

    const loadMore = useCallback(
        () =>
            dispatch(
                isMessagesPath
                    ? loadMoreJobFeedbackMessageList(appID)
                    : loadMoreJobList(appID)
            ),
        [dispatch, isMessagesPath, appID]
    )
    const navTo = useCallback(
        (path: string) => dispatch(push(path)),
        [dispatch]
    )

    const appName = useAppState(
        state => getValueById(state.api.apps.apps, appID)?.name || ""
    )

    const paramsForCount = useMemo(
        () => (isMessagesPath ? { ...params, onlyIfFeedback: true } : params),
        [isMessagesPath, params]
    )

    const count = useJobCountWithFilters(
        appID,
        paramsForCount,
        isMessagesPath ? "message" : "jobList",
        { refreshInterval: 3000 }
    )

    const [selectedJobs, toggleJobSelection, clearJobSelection] =
        useSelection<APIJobModel>(
            getJobID,
            hasValue(jobListFetch) ? jobListFetch.value.items : []
        )
    const rolesFetch = useAssumedRoles(appID)
    const roles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])

    useEffect(() => {
        if (selectedJob) {
            document
                .querySelector(`[data-item-id="${selectedJob}"]`)
                ?.scrollIntoView({ block: "center", inline: "center" })
        }
    }, [selectedJob])

    useEffect(() => {
        if (
            selected !== undefined &&
            useSelectedParam &&
            hasValue(jobListFetch)
        ) {
            const jobs = getValue(jobListFetch)
            if (!jobs) return

            selected.forEach(s => {
                if (s && !selectedJobs.find(j => j.id === s)) {
                    const job = jobs.items.find(({ id }) => s === id)
                    if (job) toggleJobSelection(job)
                }
            })
        }
    }, [
        selected,
        useSelectedParam,
        toggleJobSelection,
        selectedJobs,
        jobListFetch
    ])

    useEffect(() => {
        dispatch(getJobWithFeedbackStats(appID))
    }, [appID, jobListFetch, dispatch]) // fetch stats on job list change

    const handleToggleJobSelection = useCallback(
        (model: APIJobModel) => {
            if (selected && useSelectedParam) {
                noSelected()
                const { pathname, search } = window.location
                const s = new URLSearchParams(search)
                s.delete("selected")
                window.history.replaceState(
                    null,
                    document.title,
                    `${pathname}?${s.toString()}`
                )
            }
            if (model) toggleJobSelection(model)
        },
        [noSelected, toggleJobSelection, selected, useSelectedParam]
    )

    const handleClickRow = useCallback(
        (item: APIJobModel, isSelected: boolean) => {
            setNavigationMode(isSelected ? "selected" : "all")
            dispatch(
                push(jobsDetailPath(orgUsername, appID, item.id, filterAndSort))
            )
        },
        [dispatch, jobsDetailPath, orgUsername, appID, filterAndSort]
    )

    const handleReviewRow = useCallback(
        (item: APIJobModel, isSelected: boolean) => {
            setNavigationMode(isSelected ? "selected" : "all")
            dispatch(
                push(
                    routePaths.jobsReview(
                        orgUsername,
                        appID,
                        item.id,
                        filterAndSort
                    )
                )
            )
        },
        [dispatch, orgUsername, appID, filterAndSort]
    )

    const handleClearJobSelection = useCallback(() => {
        setNavigationMode("all")
        clearJobSelection()
    }, [clearJobSelection])

    const handleChangeSort = useCallback(
        (s: SortExpression<APIJobQueryModel>) => {
            clearJobSelection()
            navTo(
                appDetailPath(orgUsername, appID, { ...filterAndSort, sort: s })
            )
        },
        [
            clearJobSelection,
            navTo,
            appDetailPath,
            orgUsername,
            appID,
            filterAndSort
        ]
    )

    const handleChangeFilters = useCallback(
        (f: FilterExpression<APIJobQueryModel>[]) => {
            clearJobSelection()
            navTo(
                appDetailPath(orgUsername, appID, {
                    ...filterAndSort,
                    filter: f
                })
            )
        },
        [
            clearJobSelection,
            navTo,
            appDetailPath,
            orgUsername,
            appID,
            filterAndSort
        ]
    )

    const jobs = hasValue(jobListFetch) ? jobListFetch.value.items : []
    const targetJobs = selectedJobs.length === 0 ? jobs : selectedJobs

    const [canLabel, labelableJobs] = useCanLabelWebSocket(
        appID,
        paramsForCount,
        isMessagesPath ? "message" : "jobList",
        jobListFetch,
        targetJobs
    )

    const labeledSelected = useMemo(
        () =>
            selectedJobs.length > 0
                ? labelableJobs.map(j => parseInt(j.id, 10))
                : [],
        [selectedJobs.length, labelableJobs]
    )
    const handleLabel = useMemo(
        () =>
            canLabel && !isMessagesPath
                ? () =>
                      dispatch(
                          push(
                              routePaths.appDetailLabeling(
                                  orgUsername,
                                  appID,
                                  filterAndSort,
                                  labeledSelected
                              ),
                              { isMessagesPath }
                          )
                      )
                : undefined,
        [
            canLabel,
            isMessagesPath,
            dispatch,
            orgUsername,
            appID,
            filterAndSort,
            labeledSelected
        ]
    )

    const handleCancel = useMemo(
        () =>
            hasAppPermission(roles, "job:cancel") &&
            targetJobs.some(isCancelableJob)
                ? () => setDialog("cancel")
                : undefined,
        [roles, targetJobs]
    )

    const handleRelabel = useMemo(
        () =>
            hasAppPermission(roles, "job:relabel") &&
            targetJobs.some(isRelabelableJob)
                ? () => setDialog("relabel")
                : undefined,
        [roles, targetJobs]
    )

    const handleEnqueue = useMemo(
        () =>
            hasAppPermission(roles, "job:submit") &&
            (targetJobs.some(isEnqueueableJob) || (count?.PENDING ?? 0) > 0)
                ? () => setDialog("enqueue")
                : undefined,
        [roles, targetJobs, count]
    )

    const handleDelete = useMemo(
        () =>
            !isMessagesPath &&
            hasAppPermission(roles, "job:delete") &&
            targetJobs.some(isDeletableJob)
                ? () => setDialog("delete")
                : undefined,
        [isMessagesPath, roles, targetJobs]
    )

    const totalCount = useJobTotalCount(appID, isMessagesPath)
    const filteredCount = getValue(jobListFetch)?.total

    const isAppEnabled =
        hasValue(instanceFetch) && isAppInstanceEnabled(instanceFetch.value)

    const filterFields = useJobFilterParams(appID, isMessagesPath)

    const sortOptions = useMemo(() => {
        if (!hasResult(activeLearningStatusFetch)) {
            return []
        }

        const sortOptions = [
            ...(isMessagesPath ? jobWithMessagesSortOptions : jobSortOptions)
        ]
        if (
            !activeLearningStatusFetch.isError &&
            activeLearningStatusFetch.value !== "unused"
        ) {
            sortOptions.push(modelScoreSortOptions)
        }
        return sortOptions
    }, [activeLearningStatusFetch, isMessagesPath])

    const handleAddTags = useMemo(
        () =>
            hasAppPermission(roles, "job:edit-tags") &&
            filteredCount !== undefined &&
            filteredCount > 0
                ? () => setDialog("add-tags")
                : undefined,
        [filteredCount, roles]
    )

    const handleRemoveTags = useMemo(
        () =>
            hasAppPermission(roles, "job:edit-tags") &&
            filteredCount !== undefined &&
            filteredCount > 0
                ? () => setDialog("remove-tags")
                : undefined,
        [filteredCount, roles]
    )

    const handleStartNavigationSelected = useCallback(
        () => setNavigationMode("selected"),
        []
    )

    const noFilterLink =
        totalCount !== undefined &&
        filteredCount !== undefined &&
        filteredCount < totalCount &&
        filterAndSort.filter.length > 0
            ? appDetailPath(orgUsername, appID, {
                  filter: [],
                  sort: filterAndSort.sort
              })
            : undefined
    const jobIDFetch = useMemo<FetchState<string>>(
        () =>
            jobID === null
                ? hasValue(jobListFetch) &&
                  jobListFetch.value.items.length === 0
                    ? Result.error("")
                    : "loading"
                : jobID === undefined
                ? jobID
                : Result.ok(jobID),
        [jobID, jobListFetch]
    )
    const renderDetail =
        jobID === undefined
            ? undefined
            : () => {
                  const taskIDFetch: FetchState<string> =
                      taskID === null
                          ? "loading"
                          : taskID === undefined
                          ? Result.error("")
                          : Result.ok(taskID)
                  const versionFetch: FetchState<string> =
                      version === null
                          ? "loading"
                          : version === undefined
                          ? Result.error("")
                          : Result.ok(version)

                  return (
                      <DetailView
                          key={`${isReviewView}`}
                          orgUsername={orgUsername}
                          appID={appID}
                          jobIDFetch={jobIDFetch}
                          versionFetch={versionFetch}
                          taskIDFetch={taskIDFetch}
                          filterAndSort={filterAndSort}
                          clearFilterOnCloseModal={clearFilterOnCloseModal}
                          navigationItems={
                              navigationMode === "all"
                                  ? undefined
                                  : selectedJobs
                          }
                          filterFields={filterFields}
                          onClearSelection={handleClearJobSelection}
                          selectedJobs={labeledSelected}
                      />
                  )
              }

    const renderSubheader = useMemo(() => {
        if (
            !isMessagesPath ||
            !hasValue(instanceFetch) ||
            !instanceFetch.value.jobWithFeedbackStats
        ) {
            return null
        }
        return (
            <FeedbackTagsHeader
                orgUsername={orgUsername}
                feedbackTags={
                    instanceFetch.value.jobWithFeedbackStats.feedbackTagStats
                }
                sort={filterAndSort.sort}
                appID={appID}
                appliedFilters={filterAndSort.filter}
            />
        )
    }, [
        isMessagesPath,
        instanceFetch,
        orgUsername,
        filterAndSort.sort,
        filterAndSort.filter,
        appID
    ])

    useEffect(() => {
        if (
            jobID === null &&
            hasValue(jobListFetch) &&
            jobListFetch.value.items.length > 0
        ) {
            // when the job id to open is resolved, redirect to the job detail
            const nextJobID = jobListFetch.value.items[0].id
            const path = jobsDetailReviewPath(orgUsername, appID, nextJobID, {
                ...filterAndSort,
                clearFilterOnCloseModal
            })

            // NOTE: use dispatch(replace(...)) here instead of
            // returning <Redirect to={...} /> so as not to trigger DOM re-creation
            dispatch(replace(path))
        }
    }, [
        appID,
        jobID,
        jobListFetch,
        dispatch,
        filterAndSort,
        clearFilterOnCloseModal,
        jobsDetailReviewPath,
        orgUsername
    ])

    useEffect(() => {
        // reset navigation mode when job modal is closed
        if (jobID === undefined) {
            setNavigationMode("all")
        }
    }, [jobID])

    useEffect(() => {
        dispatch(startMonitorProcessingBatchList(appID))

        return () => {
            dispatch(stopMonitorProcessingBatchList())
        }
    }, [dispatch, appID])

    const containerProps: AppDetailBaseProps = {
        orgUsername,
        id: appID,
        title: isMessagesPath ? "Messages" : "Work Queue",
        breadcrumbs: [],
        fixedContentWidth: false,
        renderModalView: renderDetail,
        filterAndSort,
        forceUserExpiryContact: userExpiryContact,
        resetUserExpiryContact: hideUserExpiryContact
    }
    const asyncOperationsFetch = useAppAsyncOperations(appID)
    const handleOperationRetry = useCallback(
        async (operationID: number) =>
            dispatch(retryAsyncOperation(appID, operationID)),
        [appID, dispatch]
    )

    const handleOperationArchive = useCallback(
        async (operationID: number) =>
            dispatch(archiveAsyncOperation(appID, operationID)),
        [appID, dispatch]
    )

    const downloadOperationId = useAppState(
        state => state.api.downloadJobs[appID]?.downloadOperation?.id
    )
    const downloadOperation = useJobDownloadOperation(
        appID,
        downloadOperationId
    )

    const handleDownload = (downloadURL: string) => {
        window.open(downloadURL, "_blank", "noopener,noreferrer")
        dispatch(clearDownloadAllLink(appID))
    }

    return (
        <AppDetailBase {...containerProps}>
            {isAppEnabled && !isMessagesPath && (
                <div className="flex">
                    {hasAppPermission(roles, "job:review") && (
                        <JobsListReviewButton
                            orgUsername={orgUsername}
                            appID={appID}
                            filterAndSort={filterAndSort}
                            selectedJobs={selectedJobs}
                            jobListFetch={jobListFetch}
                            onStartNavigationSelected={
                                handleStartNavigationSelected
                            }
                        />
                    )}
                    {hasAppPermission(roles, "job:submit") && (
                        <Button
                            onClick={handleUploadData}
                            color="white"
                            className="w-64"
                            testId="job-list-upload-more"
                        >
                            Upload{" "}
                            {remaining_count === undefined
                                ? ""
                                : remaining_count + " "}
                            more data
                        </Button>
                    )}
                </div>
            )}
            {hasAppPermission(roles, "job:submit") && !isMessagesPath && (
                <BatchStatusBanner
                    className="mt-3"
                    orgUsername={orgUsername}
                    appID={appID}
                />
            )}
            {(getValue(asyncOperationsFetch) ?? []).map(operation => (
                <AsyncOperationStatusBanner
                    key={operation.id}
                    className="mt-3"
                    model={operation}
                    onOperationRetry={handleOperationRetry}
                    onOperationArchive={handleOperationArchive}
                    onDownload={handleDownload}
                />
            ))}
            {downloadOperation?.operation && (
                <AsyncOperationStatusBanner
                    key={downloadOperation.operation.id}
                    className="mt-3"
                    model={downloadOperation.operation}
                    onOperationRetry={handleOperationRetry}
                    onOperationArchive={handleOperationArchive}
                    onDownload={handleDownload}
                />
            )}
            <JobsListTable
                orgUsername={orgUsername}
                appID={appID}
                items={getValue(jobListFetch)?.items}
                totalCount={totalCount}
                filteredCount={filteredCount}
                filteredCountByStatus={count}
                onToggleRowCheck={handleToggleJobSelection}
                onRowClick={handleClickRow}
                selectedDataIds={selectedJobs.map(getJobID)}
                filterFields={filterFields}
                sortFields={sortOptions}
                filters={filterAndSort.filter}
                sort={filterAndSort.sort}
                role={roles}
                onChangeFilter={handleChangeFilters}
                onChangeSort={handleChangeSort}
                isAppEnabled={isAppEnabled}
                onCancel={handleCancel}
                onRelabel={handleRelabel}
                onEnqueue={handleEnqueue}
                onDelete={handleDelete}
                onAddTags={handleAddTags}
                onRemoveTags={handleRemoveTags}
                onDownload={confirmDownload}
                onReview={
                    hasAppPermission(roles, "job:review")
                        ? handleReviewRow
                        : undefined
                }
                onLoadMore={loadMore}
                onLabel={handleLabel}
                onVisibleItemsChanged={onVisibleItemsChanged}
                columns={columns}
                subHeader={renderSubheader}
                noFilterLink={noFilterLink}
                activeLearningStatusFetch={activeLearningStatusFetch}
                fixedLayout={isMessagesPath}
            />
            {dialog !== undefined && (
                <JobsBatchActionModal
                    type={dialog}
                    appID={appID}
                    appName={appName}
                    params={params}
                    countByStatus={count}
                    selectedJobs={selectedJobs}
                    filteredCount={filteredCount ?? 0}
                    onClose={hideDialog}
                    onActionDone={handleClearJobSelection}
                    isMessages={isMessagesPath}
                />
            )}
            {batchInProgressDialog && (
                <Confirm
                    title="Job upload in progress"
                    description="Try again after the previous upload is processed"
                    yesLabel="OK"
                    onClickYes={hideBatchInProgressDialog}
                    onClose={hideBatchInProgressDialog}
                />
            )}
        </AppDetailBase>
    )
}
